import React, { useState, useEffect } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import {
  TrendingDown,
  ArrowRight,
  Users,
  ShoppingCart,
  CreditCard,
  MessageSquare,
  Clock,
  Zap,
  RefreshCw
} from 'lucide-react';
import { FaWhatsapp, FaInstagram, FaFacebookMessenger, FaGlobe } from 'react-icons/fa';

// Conversion Funnel Component
export const ConversionFunnel = ({ period = "today", channel = "all" }) => {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchFunnel = async () => {
      // Wait for user to be available
      if (!user?.id) {
        // Don't set loading false immediately, wait a bit
        const timeout = setTimeout(() => setLoading(false), 1000);
        return () => clearTimeout(timeout);
      }
      
      setLoading(true);
      try {
        setError(false);
        console.log('[Funnel] Fetching for user:', user.id, 'period:', period, 'channel:', channel);
        const res = await axios.get(`${API}/dashboard/funnel?user_id=${user.id}&period=${period}&channel=${channel}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('[Funnel] Response:', res.data);
        if (res.data.success) {
          setData(res.data);
        }
      } catch (err) {
        console.error('[Funnel] Error:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchFunnel();
  }, [user?.id, period, channel]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-2 sm:space-y-3">
        <div className="h-3 sm:h-4 bg-slate-200 dark:bg-zinc-800 rounded w-1/3"></div>
        <div className="h-16 sm:h-20 bg-slate-200 dark:bg-zinc-800 rounded"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-3 sm:space-y-4">
        <h4 className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-zinc-200">Funnel Konversi</h4>
        <div className="text-center py-4 sm:py-6 text-slate-500 text-[10px] sm:text-xs dark:text-zinc-400">
          <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 opacity-30" />
          <p>Belum ada data chat</p>
        </div>
      </div>
    );
  }

  const { funnel, conversion_rates } = data;
  
  // Period label for subtitle
  const periodLabel = {
    today: 'hari ini',
    week: '7 hari terakhir',
    month: '30 hari terakhir',
    all: 'sepanjang waktu'
  }[period] || period;
  
  const stages = [
    { key: 'chat_masuk', label: 'Chat', icon: MessageSquare, color: 'bg-indigo-500', value: funnel.chat_masuk },
    { key: 'minat', label: 'Minat', icon: Users, color: 'bg-amber-600', value: funnel.minat },
    { key: 'order', label: 'Order', icon: ShoppingCart, color: 'bg-purple-500', value: funnel.order },
    { key: 'dibayar', label: 'Dibayar', icon: CreditCard, color: 'bg-green-600', value: funnel.dibayar },
  ];

  // Always show the funnel visualization, even with 0 values
  // Set a minimum width for visibility
  const maxValue = Math.max(...stages.map(s => s.value), 1);
  const hasAnyData = stages.some(s => s.value > 0);

  return (
    <div className="space-y-3 sm:space-y-4" data-testid="conversion-funnel">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-zinc-200">Funnel Konversi</h4>
          <p className="text-[10px] sm:text-xs text-slate-500 dark:text-zinc-500 mt-0.5" title="Ganti periode di dropdown kanan atas Dashboard" data-testid="funnel-period-label">
            Periode: {periodLabel}
          </p>
        </div>
        <span className="text-[10px] sm:text-xs text-slate-500 bg-slate-100 dark:bg-zinc-800 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full dark:text-zinc-400">
          CR: {conversion_rates.overall}%
        </span>
      </div>
      
      {/* Visual Funnel - Always show, with 0 values if no data */}
      <div className="space-y-1.5 sm:space-y-2">
        {stages.map((stage, idx) => {
          // If no data, show decreasing widths for funnel shape (4 stages now)
          const defaultWidths = [100, 75, 50, 30];
          const width = hasAnyData 
            ? Math.max((stage.value / maxValue) * 100, 15)
            : defaultWidths[idx];
          const Icon = stage.icon;
          return (
            <div key={stage.key}>
              <div 
                className={`${hasAnyData ? stage.color : 'bg-slate-200 dark:bg-zinc-800'} h-8 sm:h-10 rounded-lg flex items-center justify-between px-3 sm:px-4 transition-all duration-500`}
                style={{ width: `${width}%`, minWidth: '120px' }}
              >
                <div className={`flex items-center gap-1.5 sm:gap-2 ${hasAnyData ? 'text-white' : 'text-slate-500 dark:text-zinc-500'}`}>
                  <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="text-[10px] sm:text-xs font-medium">{stage.label}</span>
                </div>
                <span className={`font-bold text-xs sm:text-sm ml-3 ${hasAnyData ? 'text-white' : 'text-slate-500 dark:text-zinc-500'}`}>{stage.value}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Conversion Rates - Better spacing */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 pt-3 sm:pt-4 border-t border-slate-100 dark:border-zinc-800">
        <div className="text-center py-1">
          <p className="text-[10px] sm:text-xs text-slate-500 mb-1 dark:text-zinc-400">Chat → Minat</p>
          <p className="text-xs sm:text-sm font-bold text-slate-700 dark:text-zinc-200">{conversion_rates.chat_to_interest || 0}%</p>
        </div>
        <div className="text-center py-1">
          <p className="text-[10px] sm:text-xs text-slate-500 mb-1 dark:text-zinc-400">Minat → Order</p>
          <p className="text-xs sm:text-sm font-bold text-slate-700 dark:text-zinc-200">{conversion_rates.interest_to_order || 0}%</p>
        </div>
        <div className="text-center py-1">
          <p className="text-[10px] sm:text-xs text-slate-500 mb-1 dark:text-zinc-400">Order → Bayar</p>
          <p className="text-xs sm:text-sm font-bold text-slate-700 dark:text-zinc-200">{conversion_rates.order_to_paid || 0}%</p>
        </div>
      </div>
    </div>
  );
};

// Channel Performance Table
export const ChannelPerformance = ({ period = "today", channel = "all" }) => {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchChannels = async () => {
      if (!user?.id) {
        const timeout = setTimeout(() => setLoading(false), 1000);
        return () => clearTimeout(timeout);
      }
      
      setLoading(true);
      try {
        setError(false);
        console.log('[Channel] Fetching for user:', user.id, 'channel:', channel);
        const res = await axios.get(`${API}/dashboard/channel-performance?user_id=${user.id}&period=${period}&channel=${channel}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('[Channel] Response:', res.data);
        if (res.data.success) {
          setData(res.data.channels);
        }
      } catch (err) {
        console.error('[Channel] Error:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchChannels();
  }, [user?.id, period, channel]);

  const getChannelIcon = (channel) => {
    const icons = {
      'Messenger': FaFacebookMessenger,
      'Instagram': FaInstagram,
      'Whatsapp': FaWhatsapp,
      'Website': FaGlobe
    };
    return icons[channel] || FaGlobe;
  };

  const getChannelColor = (channel) => {
    const colors = {
      'Messenger': 'text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-950/40',
      'Instagram': 'text-pink-600 dark:text-pink-400 bg-pink-100 dark:bg-pink-900/30',
      'Whatsapp': 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30',
      'Website': 'text-slate-600 bg-slate-100 dark:bg-zinc-800'
    };
    return colors[channel] || 'text-slate-600 bg-slate-100';
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-8 sm:h-10 bg-slate-200 dark:bg-zinc-800 rounded"></div>
        ))}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-2 sm:space-y-3">
        <h4 className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-zinc-200">Performa Channel</h4>
        <div className="text-center py-4 sm:py-6 text-slate-500 text-[10px] sm:text-xs dark:text-zinc-400">
          <FaGlobe className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 opacity-30" />
          <p>Belum ada data channel</p>
        </div>
      </div>
    );
  }

  // Check if there's any real data
  const hasAnyData = data.some(ch => ch.chat > 0 || ch.order > 0);

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(num || 0);
  };

  return (
    <div className="space-y-2 sm:space-y-3">
      <h4 className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-zinc-200">Performa Channel</h4>
      
      {/* Desktop: Table view */}
      <div className="hidden sm:block w-full">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-slate-500 border-b border-slate-100 dark:border-zinc-800 dark:text-zinc-400">
              <th className="text-left py-2 font-medium">Channel</th>
              <th className="text-center py-2 font-medium">Chat</th>
              <th className="text-center py-2 font-medium">Order</th>
              <th className="text-center py-2 font-medium">CR</th>
              <th className="text-right py-2 font-medium pl-2">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {data.filter(ch => ch.channel !== 'Telegram').map((ch) => {
              const Icon = getChannelIcon(ch.channel);
              const colorClass = getChannelColor(ch.channel);
              return (
                <tr key={ch.channel} className="border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-zinc-900/50 dark:bg-zinc-900">
                  <td className="py-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${colorClass}`}>
                        <Icon className="w-3 h-3" />
                      </div>
                      <span className="font-medium text-slate-700 dark:text-zinc-200 truncate">{ch.channel}</span>
                    </div>
                  </td>
                  <td className="text-center py-2 text-slate-600 dark:text-zinc-300">{ch.chat}</td>
                  <td className="text-center py-2 text-slate-600 dark:text-zinc-300">{ch.order}</td>
                  <td className="text-center py-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${ch.conversion_rate >= 10 ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400' : 'bg-slate-100 text-slate-600 dark:bg-zinc-800 dark:text-zinc-300'}`}>
                      {ch.conversion_rate}%
                    </span>
                  </td>
                  <td className="text-right py-2 font-medium text-slate-700 dark:text-zinc-200 whitespace-nowrap pl-2">
                    {ch.revenue > 0 ? formatCurrency(ch.revenue) : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile: Card view */}
      <div className="sm:hidden space-y-2">
        {data.filter(ch => ch.channel !== 'Telegram').map((ch) => {
          const Icon = getChannelIcon(ch.channel);
          const colorClass = getChannelColor(ch.channel);
          return (
            <div key={ch.channel} className="flex items-center justify-between py-1.5 border-b border-slate-50 dark:border-slate-800">
              <div className="flex items-center gap-2 min-w-0">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${colorClass}`}>
                  <Icon className="w-2.5 h-2.5" />
                </div>
                <div className="min-w-0">
                  <span className="text-[11px] font-medium text-slate-700 dark:text-zinc-200">{ch.channel}</span>
                  <div className="flex gap-2 text-[9px] text-slate-500 dark:text-zinc-400">
                    <span>{ch.chat} chat</span>
                    <span>{ch.order} order</span>
                    <span className={ch.conversion_rate >= 10 ? 'text-indigo-600 font-medium' : ''}>{ch.conversion_rate}% CR</span>
                  </div>
                </div>
              </div>
              <span className="text-[11px] font-semibold text-slate-700 dark:text-zinc-200 whitespace-nowrap shrink-0 ml-2">
                {ch.revenue > 0 ? formatCurrency(ch.revenue) : '-'}
              </span>
            </div>
          );
        })}
      </div>
      
      {data.filter(ch => ch.channel !== 'Telegram').every(ch => ch.chat === 0) && (
        <p className="text-center text-[10px] sm:text-xs text-slate-500 py-1 sm:py-2 dark:text-zinc-400">Belum ada data chat</p>
      )}
    </div>
  );
};

// Customer Insights Card
export const CustomerInsights = ({ period = "month", channel = "all" }) => {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchInsights = async () => {
      if (!user?.id) {
        const timeout = setTimeout(() => setLoading(false), 1000);
        return () => clearTimeout(timeout);
      }
      
      setLoading(true);
      try {
        setError(false);
        console.log('[CustomerInsights] Fetching for user:', user.id, 'channel:', channel);
        const res = await axios.get(`${API}/dashboard/customer-insights?user_id=${user.id}&period=${period}&channel=${channel}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('[CustomerInsights] Response:', res.data);
        if (res.data.success) {
          setData(res.data.insights);
        }
      } catch (err) {
        console.error('[CustomerInsights] Error:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchInsights();
  }, [user?.id, period, channel]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-2 sm:space-y-3">
        <div className="h-3 sm:h-4 bg-slate-200 dark:bg-zinc-800 rounded w-1/2"></div>
        <div className="h-12 sm:h-16 bg-slate-200 dark:bg-zinc-800 rounded"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-3 sm:space-y-4">
        <h4 className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-zinc-200">Customer Insights</h4>
        <div className="text-center py-4 sm:py-6 text-slate-500 text-[10px] sm:text-xs dark:text-zinc-400">
          <Users className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 opacity-30" />
          <p>Belum ada data customer</p>
        </div>
      </div>
    );
  }

  // Check if there's actual customer data
  const hasCustomers = data.total_customers > 0;

  if (!hasCustomers) {
    return (
      <div className="space-y-3 sm:space-y-4">
        <h4 className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-zinc-200">Customer Insights</h4>
        <div className="text-center py-4 sm:py-6 text-slate-500 text-[10px] sm:text-xs dark:text-zinc-400">
          <Users className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 opacity-30" />
          <p>Belum ada customer</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      <h4 className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-zinc-200">Customer Insights</h4>
      
      {/* New vs Repeat Visual */}
      <div className="flex items-center gap-2 sm:gap-3">
        <div className="flex-1">
          <div className="flex h-2 sm:h-3 rounded-full overflow-hidden bg-slate-200 dark:bg-zinc-800">
            <div 
              className="bg-indigo-500 transition-all duration-500"
              style={{ width: `${data.new_percentage}%` }}
            />
            <div 
              className="bg-indigo-500 transition-all duration-500"
              style={{ width: `${data.repeat_percentage}%` }}
            />
          </div>
        </div>
        <span className="text-[10px] sm:text-xs text-slate-500 w-12 sm:w-16 text-right dark:text-zinc-400">{data.total_customers} total</span>
      </div>
      
      {/* Legend - Stack on very small screens */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-indigo-500"></div>
          <div>
            <p className="text-[10px] sm:text-xs text-slate-500 dark:text-zinc-400">Baru</p>
            <p className="text-xs sm:text-sm font-bold text-slate-700 dark:text-zinc-200">{data.new_customers} <span className="text-[10px] font-normal text-slate-500 dark:text-zinc-400">({data.new_percentage}%)</span></p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-indigo-500"></div>
          <div>
            <p className="text-[10px] sm:text-xs text-slate-500 dark:text-zinc-400">Repeat</p>
            <p className="text-xs sm:text-sm font-bold text-slate-700 dark:text-zinc-200">{data.repeat_customers} <span className="text-[10px] font-normal text-slate-500 dark:text-zinc-400">({data.repeat_percentage}%)</span></p>
          </div>
        </div>
      </div>

      {/* Top Customers */}
      {data.top_customers?.length > 0 && (
        <div className="pt-2 sm:pt-3 border-t border-slate-100 dark:border-zinc-800">
          <p className="text-[10px] sm:text-xs text-slate-500 mb-1.5 sm:mb-2 dark:text-zinc-400">Top Customer</p>
          <div className="space-y-1 sm:space-y-1.5">
            {data.top_customers.slice(0, 3).map((c, idx) => (
              <div key={idx} className="flex items-center justify-between gap-2 text-[10px] sm:text-xs">
                <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                  <span className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center text-[10px] font-bold text-slate-500 shrink-0 dark:text-zinc-400">
                    {idx + 1}
                  </span>
                  <span className="text-slate-700 dark:text-zinc-200 truncate">{c.name}</span>
                </div>
                <span className="text-slate-500 whitespace-nowrap shrink-0 dark:text-zinc-400">{c.orders}x • Rp {(c.total || 0).toLocaleString('id-ID')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// SLA Response Time Card
export const ResponseTimeCard = ({ period = "today", channel = "all" }) => {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) {
        const timeout = setTimeout(() => setLoading(false), 1000);
        return () => clearTimeout(timeout);
      }
      
      setLoading(true);
      try {
        setError(false);
        console.log('[ResponseTime] Fetching for user:', user.id, 'channel:', channel);
        const res = await axios.get(`${API}/dashboard/response-time?user_id=${user.id}&period=${period}&channel=${channel}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('[ResponseTime] Response:', res.data);
        if (res.data.success) {
          setData(res.data.stats);
        }
      } catch (err) {
        console.error('[ResponseTime] Error:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.id, period, channel]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-2">
        <div className="h-3 sm:h-4 bg-slate-200 dark:bg-zinc-800 rounded w-1/3"></div>
        <div className="h-10 sm:h-12 bg-slate-200 dark:bg-zinc-800 rounded"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-2 sm:space-y-3">
        <h4 className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-zinc-200">Response Time</h4>
        <div className="text-center py-4 sm:py-6 text-slate-500 text-[10px] sm:text-xs dark:text-zinc-400">
          <Clock className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 opacity-30" />
          <p>Belum ada data</p>
        </div>
      </div>
    );
  }

  // Check if there's any conversation data
  const hasConversations = data.total_conversations > 0;

  // Layout consistent with KPICard
  return (
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-[10px] sm:text-xs text-slate-500 dark:text-zinc-400 font-medium uppercase tracking-wide">
          Response Time
        </p>
        <p className={`font-bold text-xs sm:text-lg mt-1 ${hasConversations ? 'text-slate-900 dark:text-zinc-50' : 'text-slate-500 dark:text-zinc-500'}`}>
          {data.avg_response_time_display || '0s'}
        </p>
        
        {/* Trend indicator - same style as KPICard compact */}
        <div className="flex items-center gap-1 mt-2 flex-nowrap">
          <span className={`inline-flex items-center gap-0.5 px-1 sm:px-1.5 py-0.5 rounded text-[9px] sm:text-[10px] font-medium whitespace-nowrap ${
            !hasConversations ? 'text-slate-500 bg-slate-50 dark:bg-zinc-800' :
            data.within_5min_percentage >= 90 ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20' : 
            'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20'
          } dark:text-green-400`}>
            <Clock className="w-3 h-3 flex-shrink-0" />
            {data.within_5min_percentage}%
          </span>
          <span className="text-[9px] sm:text-[10px] text-slate-500 whitespace-nowrap dark:text-zinc-400">&lt; 5m SLA</span>
        </div>
      </div>
      
      {/* Icon - same style as KPICard */}
      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center flex-shrink-0">
        <Clock className="w-4 h-4 text-white" />
      </div>
    </div>
  );
};

export default { ConversionFunnel, ChannelPerformance, CustomerInsights, ResponseTimeCard };
